import React, { useState, forwardRef, useImperativeHandle } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Checkbox,
  Typography,
  TextField,
  IconButton,
  FormControlLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import { NAICS_MAPS } from '../../utils/constants/Naics'; // Import NAICS_MAPS

// Helper function to split and add keywords
const addKeywords = (text) => text.toLowerCase().split(/\s+/);

const NaicsAccordion = forwardRef(({ keywords, setKeywords }, ref) => {
  const [checkedNaics, setCheckedNaics] = useState([]);
  const [filteredNaics, setFilteredNaics] = useState(NAICS_MAPS);
  const [searchTerm, setSearchTerm] = useState('');

  // Handle checkbox change
  const handleNaicsChange = (naics) => {
    const naicsKeywords = [...addKeywords(naics.code.toString()), ...addKeywords(naics.description)];

    setCheckedNaics((prev) =>
      prev.includes(naics)
        ? prev.filter((code) => code !== naics) // Remove unchecked
        : [...prev, naics] // Add checked
    );

    setKeywords((prev) => {
      // If unchecked, remove from keywords
      if (checkedNaics.includes(naics)) {
        return prev.filter((kw) => !naicsKeywords.includes(kw));
      }
      // If checked, add both code and description to keywords
      return [...prev, ...naicsKeywords];
    });
  };

  // Filter NAICS based on search term
  const handleSearchChange = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    setFilteredNaics(
      NAICS_MAPS.filter(
        (naics) =>
          naics.code.toString().includes(term) || naics.description.toLowerCase().includes(term)
      )
    );
  };

  // Clear search term
  const clearSearch = () => {
    setSearchTerm('');
    setFilteredNaics(NAICS_MAPS);
  };

  // Expose reset functionality to parent (Sidebar.js)
  useImperativeHandle(ref, () => ({
    resetCheckedNaics: () => {
      setCheckedNaics([]);
      setSearchTerm('');
      setFilteredNaics(NAICS_MAPS);
    },
  }));

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#f5f5f5' }}>
        {/* Badge with Checked Count */}
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            backgroundColor: checkedNaics.length === 0 ? 'gray' : '#00274D', // Gray if count is 0
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: 'white' }}>
            {checkedNaics.length}
          </Typography>
        </Box>
        <Typography>NAICS Code</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {/* Search Bar with Clear Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            label="Search NAICS Code/Description"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <IconButton onClick={clearSearch} sx={{ ml: 1 }}>
            <ClearIcon />
          </IconButton>
        </Box>

        {/* Scrollable Table with Checkboxes */}
        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
          <Table size="small">
            <TableBody>
              {filteredNaics.map((naics, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkedNaics.includes(naics)}
                          onChange={() => handleNaicsChange(naics)}
                        />
                      }
                      label={`${naics.code} - ${naics.description}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
});

export default NaicsAccordion;